import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  GanttChart,
  Bell,
  BarChart3,
  GitBranch,
  FolderTree,
  Users,
  Building2,
  Settings,
  Cog,
  LogOut,
  Menu,
  X,
  HelpCircle,
  Moon,
  Sun,
  Search,
  Shield,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pin,
  PinOff,
  ShieldCheck,
  Bug,
  Upload,
} from 'lucide-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useThemeStore } from '../../stores/themeStore';
import { useProjectSelectionStore } from '../../stores/projectSelectionStore';
import { cn } from '../../utils/cn';
import { OnboardingOverlay } from '../onboarding/OnboardingOverlay';
import { CommandPalette } from '../ui/CommandPalette';
import { KeyboardShortcutsHelp } from '../ui/KeyboardShortcutsHelp';
import { QuickActionFAB } from '../ui/QuickActionFAB';
import { useNavigationShortcuts } from '../../hooks/useNavigationShortcuts';
import { AiChatWidget } from '../ai/AiChatWidget';
import { ActivityDropdown } from '../activity/ActivityDropdown';
import { api } from '../../services/api';

// Map paths to onboarding data attributes
const onboardingKeys: Record<string, string> = {
  '/': 'dashboard',
  '/home': 'project-home',
  '/projects': 'projects',
  '/my-tasks': 'my-tasks',
  '/gantt': 'gantt',
  '/notifications': 'notifications',
  '/settings': 'settings',
};

export function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { unreadCount, pendingApprovalCount, actionCount, fetchUnreadCount } = useNotificationStore();
  const { theme, toggleTheme } = useThemeStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(() => localStorage.getItem('sidebar-pinned') === 'true');
  const [sidebarHover, setSidebarHover] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useNavigationShortcuts();

  // Project header bar state
  const { selectedProject, selectProject: storeSelectProject, clearSelection } = useProjectSelectionStore();
  const [projectPopupOpen, setProjectPopupOpen] = useState(false);
  const [projectList, setProjectList] = useState<Array<{id: string; title: string; client_name?: string; status: string; priority: string; due_date?: string; overall_progress?: number}>>([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectSort, setProjectSort] = useState<'default' | 'priority' | 'due_date'>('default');

  useEffect(() => {
    fetchUnreadCount();
    let interval = setInterval(fetchUnreadCount, 10000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        fetchUnreadCount();
        interval = setInterval(fetchUnreadCount, 10000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount]);

  // Close user menu on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close admin dropdown on click outside
  useEffect(() => {
    if (!adminDropdownOpen) return;
    function handleClick(e: MouseEvent) {
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target as Node)) {
        setAdminDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [adminDropdownOpen]);

  // Fetch project list on mount for auto-select
  useEffect(() => {
    api.getProjects({ per_page: '100' }).then(r => {
      if (r.success && r.data) setProjectList(r.data as any);
    });
  }, []);

  // Also refresh when popup opens
  useEffect(() => {
    if (projectPopupOpen) {
      api.getProjects({ per_page: '100' }).then(r => {
        if (r.success && r.data) setProjectList(r.data as any);
      });
    }
  }, [projectPopupOpen]);

  // Auto-select project from URL
  useEffect(() => {
    const match = location.pathname.match(/^\/projects\/([^/]+)/);
    if (match && projectList.length > 0) {
      const projectId = match[1];
      if (projectId !== 'new') {
        const found = projectList.find(p => p.id === projectId);
        if (found && (!selectedProject || selectedProject.id !== found.id)) {
          selectProjectSilent(found);
        }
      }
    }
  }, [location.pathname, projectList]);

  // Close popup on Escape
  useEffect(() => {
    if (!projectPopupOpen) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setProjectPopupOpen(false);
    }
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [projectPopupOpen]);

  // Select project and navigate
  function selectProject(p: {id: string; title: string; client_name?: string}) {
    storeSelectProject(p);
    setProjectPopupOpen(false);
    navigate(`/projects/${p.id}`);
  }

  // Select project without navigating (for quick nav links)
  function selectProjectSilent(p: {id: string; title: string; client_name?: string}) {
    storeSelectProject(p);
  }

  // Priority order for sorting
  const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

  // Filtered & sorted projects for popup
  const filteredPopupProjects = useMemo(() => {
    let result = projectList;
    if (projectSearchInput) {
      const q = projectSearchInput.toLowerCase();
      result = result.filter(p =>
        p.title.toLowerCase().includes(q) ||
        (p.client_name && p.client_name.toLowerCase().includes(q))
      );
    }
    // Sort
    if (projectSort === 'priority') {
      result = [...result].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9));
    } else if (projectSort === 'due_date') {
      result = [...result].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
    }
    return result;
  }, [projectList, projectSearchInput, projectSort]);

  // Separate active vs completed/cancelled
  const activeProjects = useMemo(() => filteredPopupProjects.filter(p => p.status !== 'completed' && p.status !== 'cancelled'), [filteredPopupProjects]);
  const doneProjects = useMemo(() => filteredPopupProjects.filter(p => p.status === 'completed' || p.status === 'cancelled'), [filteredPopupProjects]);

  // Priority badge colors
  const POPUP_PRIORITY_COLORS: Record<string, string> = {
    critical: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-blue-500 text-white',
    low: 'bg-gray-500 text-white',
  };
  const POPUP_PRIORITY_LABELS: Record<string, string> = {
    critical: '緊急', high: '高', medium: '中', low: '低',
  };
  const POPUP_STATUS_LABELS: Record<string, string> = {
    not_started: '未着手', in_progress: '進行中', completed: '完了', on_hold: '保留', cancelled: 'キャンセル',
  };
  const POPUP_STATUS_COLORS: Record<string, string> = {
    not_started: 'bg-gray-500/80 text-gray-200',
    in_progress: 'bg-blue-500/80 text-white',
    completed: 'bg-green-600/80 text-white',
    on_hold: 'bg-amber-500/80 text-white',
    cancelled: 'bg-gray-400/80 text-gray-200',
  };

  // Render single project row
  function renderProjectRow(p: typeof projectList[0], isDone: boolean) {
    const daysUntilDue = p.due_date ? Math.floor((new Date(p.due_date).setHours(0,0,0,0) - new Date().setHours(0,0,0,0)) / (1000*60*60*24)) : null;
    const isOverdue = daysUntilDue !== null && daysUntilDue < 0 && !isDone;
    const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 7 && !isDone;
    const progress = p.overall_progress ?? 0;

    // Avatar color based on hash of title
    const AVATAR_COLORS = ['bg-blue-600', 'bg-emerald-600', 'bg-violet-600', 'bg-rose-600', 'bg-amber-600', 'bg-cyan-600', 'bg-pink-600', 'bg-teal-600'];
    let hash = 0;
    for (let i = 0; i < p.title.length; i++) hash = ((hash << 5) - hash + p.title.charCodeAt(i)) | 0;
    const avatarColor = AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];

    return (
      <div
        key={p.id}
        className={cn(
          'group/prow px-4 py-3 border-b border-gray-600/30 dark:border-gray-700/30 hover:bg-gray-600/40 dark:hover:bg-gray-700/40 transition-colors cursor-pointer',
          isDone && 'opacity-50'
        )}
        onClick={() => selectProject(p)}
      >
        <div className="flex items-center gap-3">
          {/* Avatar with color */}
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0', avatarColor)}>
            {(p.client_name || p.title).charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            {/* Title + badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white font-semibold text-sm truncate">{p.title}</span>
              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', POPUP_STATUS_COLORS[p.status] || 'bg-gray-500 text-white')}>
                {POPUP_STATUS_LABELS[p.status] || p.status}
              </span>
              {p.priority !== 'medium' && (
                <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', POPUP_PRIORITY_COLORS[p.priority] || 'bg-gray-500 text-white')}>
                  {POPUP_PRIORITY_LABELS[p.priority] || p.priority}
                </span>
              )}
              {isOverdue && (
                <span className="text-[10px] font-bold text-red-400">{Math.abs(daysUntilDue!)}日超過</span>
              )}
              {isDueSoon && (
                <span className="text-[10px] font-medium text-amber-400">残{daysUntilDue}日</span>
              )}
            </div>
            {/* Client name + progress + due date */}
            <div className="flex items-center gap-2 mt-1">
              {p.client_name && (
                <span className="text-xs text-gray-400 truncate max-w-[120px]">{p.client_name}</span>
              )}
              {!isDone && progress > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-16 h-1.5 bg-gray-500/50 rounded-full overflow-hidden">
                    <div className={cn('h-full rounded-full', progress >= 100 ? 'bg-green-500' : 'bg-blue-400')} style={{ width: `${Math.min(progress, 100)}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-400">{progress}%</span>
                </div>
              )}
              {p.due_date && !isOverdue && !isDueSoon && (
                <span className="text-[10px] text-gray-500">{new Date(p.due_date).toLocaleDateString('ja-JP')}</span>
              )}
            </div>
          </div>
          {/* Quick links (compact, hover-visible) */}
          <div className="flex items-center gap-1 opacity-0 group-hover/prow:opacity-100 transition-opacity shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); selectProjectSilent(p); navigate(`/projects/${p.id}/wbs`); setProjectPopupOpen(false); }}
              className="text-[10px] text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-gray-500/50"
            >
              WBS
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); selectProjectSilent(p); navigate('/gantt'); setProjectPopupOpen(false); }}
              className="text-[10px] text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-gray-500/50"
            >
              ガント
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Search icon handler
  function handleProjectSearch() {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isApprover = user?.role === 'approver' || isAdmin;

  // Main navigation items (visible to all)
  const mainNavItems = [
    { to: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/projects', icon: FolderKanban, label: t('nav.projects') },
    { to: '/home', icon: Home, label: t('nav.projectHome') },
    {
      to: selectedProject ? `/projects/${selectedProject.id}/wbs` : '/projects',
      icon: FolderTree,
      label: t('nav.wbs'),
    },
    { to: '/issues', icon: Bug, label: t('nav.issues', '課題') },
    { to: '/my-tasks', icon: ListTodo, label: t('nav.myTasks') },
    { to: '/gantt', icon: GanttChart, label: t('nav.gantt') },
    {
      to: '/notifications',
      icon: Bell,
      label: t('nav.notifications'),
      badge: unreadCount.total > 0 ? unreadCount.total : undefined,
    },
  ];

  mainNavItems.push({ to: '/reports', icon: BarChart3, label: t('nav.reports') });

  // Admin section items (approver/admin only)
  const adminNavItems: Array<{ to: string; icon: typeof LayoutDashboard; label: string }> = [];
  if (isApprover) {
    adminNavItems.push({ to: '/admin/flows', icon: GitBranch, label: t('nav.flows') });
    adminNavItems.push({ to: '/admin/wbs-templates', icon: FolderTree, label: t('nav.wbsTemplates') });
    adminNavItems.push({ to: '/admin/clients', icon: Building2, label: t('nav.clients') });
  }
  if (isAdmin) {
    adminNavItems.push({ to: '/admin/users', icon: Users, label: t('nav.users') });
    adminNavItems.push({ to: '/admin/settings', icon: Cog, label: t('nav.systemSettings') });
    adminNavItems.push({ to: '/admin/import', icon: Upload, label: t('nav.import', 'データインポート') });
  }

  const hasAdminSection = adminNavItems.length > 0;

  // Bottom items
  const bottomNavItems = [
    { to: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  // Admin section open state (persisted + auto-open when on admin page)
  const isOnAdminPage = location.pathname.startsWith('/admin');
  const [adminOpen, setAdminOpen] = useState(() => {
    if (location.pathname.startsWith('/admin')) return true;
    return false;
  });

  const toggleAdminOpen = () => {
    const next = !adminOpen;
    setAdminOpen(next);
    localStorage.setItem('admin-menu-open', String(next));
  };

  // Auto-expand when navigating to admin page
  useEffect(() => {
    if (isOnAdminPage && !adminOpen) {
      setAdminOpen(true);
    }
  }, [isOnAdminPage]);

  const hideSidebar = location.pathname === '/' || location.pathname === '/projects';
  const sidebarExpanded = sidebarPinned || sidebarHover;

  const toggleSidebarPin = () => {
    const next = !sidebarPinned;
    setSidebarPinned(next);
    localStorage.setItem('sidebar-pinned', String(next));
  };

  const handleSidebarMouseEnter = () => {
    if (sidebarPinned) return;
    clearTimeout(hoverTimeoutRef.current);
    setSidebarHover(true);
  };

  const handleSidebarMouseLeave = () => {
    if (sidebarPinned) return;
    hoverTimeoutRef.current = setTimeout(() => setSidebarHover(false), 200);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Global Header */}
      <header className="h-12 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 bg-white dark:bg-gray-900 z-30 shrink-0">
        {/* Mobile menu button */}
        <button
          className="lg:hidden mr-3 dark:text-gray-400"
          onClick={() => setSidebarOpen(true)}
          aria-expanded={sidebarOpen}
          aria-label={t('nav.openMenu')}
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="text-lg font-bold text-blue-600 dark:text-blue-400">ProcBase</span>
        </Link>

        {/* Desktop header nav - Dashboard & Projects only */}
        <nav className="hidden lg:flex items-center gap-0.5 ml-3">
          {mainNavItems.filter(item => item.to === '/' || item.to === '/projects').map((item) => {
            const isActive = item.to === '/'
              ? location.pathname === '/'
              : location.pathname === item.to || location.pathname.startsWith(item.to + '/');
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap',
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                )}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex-1" />

        {/* Search trigger - centered */}
        <button
          onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors w-64 md:w-80"
        >
          <Search className="w-3.5 h-3.5" />
          <span className="hidden md:inline">{t('common.search')}</span>
          <span className="flex-1" />
          <kbd className="hidden md:inline text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">Ctrl+K</kbd>
        </button>

        <div className="flex-1" />

        {/* Action count badge - all roles */}
        {actionCount > 0 && (
          <NavLink to="/my-tasks" className="flex items-center gap-1.5 px-2.5 py-1 mr-2 text-xs text-amber-600 dark:text-amber-400 font-medium bg-amber-50 dark:bg-amber-900/30 rounded-full border border-amber-200 dark:border-amber-800">
            <ListTodo className="w-3.5 h-3.5" />
            <span>{actionCount}件の対応</span>
          </NavLink>
        )}

        {/* Help */}
        <NavLink
          to="/help"
          className="p-2 mr-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          title={t('nav.help')}
        >
          <HelpCircle className="w-5 h-5" />
        </NavLink>

        {/* Notification bell */}
        <NavLink
          to="/notifications"
          className="relative p-2 mr-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          title={t('nav.notifications')}
          aria-label={t('nav.notifications')}
        >
          <Bell className="w-5 h-5" />
          {unreadCount.total > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
              {unreadCount.total > 99 ? '99+' : unreadCount.total}
            </span>
          )}
        </NavLink>

        {/* Activity dropdown */}
        <ActivityDropdown />

        {/* Theme toggle */}
        <button
          onClick={() => toggleTheme()}
          className="p-2 mr-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          title={theme === 'light' ? t('settings_theme.switchToDark') : t('settings_theme.switchToLight')}
          aria-label={theme === 'light' ? t('settings_theme.switchToDark') : t('settings_theme.switchToLight')}
        >
          {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
        </button>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={t('nav.userMenu', 'ユーザーメニュー')}
          >
            <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-700 dark:text-blue-300 text-sm font-bold">
              {user?.name?.charAt(0) || '?'}
            </div>
            <span className="hidden md:inline text-sm font-medium text-gray-700 dark:text-gray-300">{user?.name}</span>
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
              <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm font-medium dark:text-gray-200">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.role}</p>
              </div>
              <NavLink
                to="/settings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Settings className="w-4 h-4" />
                {t('nav.settings')}
              </NavLink>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Desktop Sidebar - dark, connected design */}
        <aside
          onMouseEnter={handleSidebarMouseEnter}
          onMouseLeave={handleSidebarMouseLeave}
          className={cn(
            'flex-col bg-[#555] shrink-0 z-20 transition-all duration-200 ease-in-out overflow-hidden',
            hideSidebar ? 'hidden' : 'hidden lg:flex',
            sidebarExpanded ? 'w-52' : 'w-14'
          )}
        >
          {/* Sidebar toggle - matches Project Header Bar h-10 */}
          <div className="h-10 flex items-center justify-end px-2 shrink-0 border-b border-[#666]">
            {sidebarExpanded ? (
              <button
                onClick={toggleSidebarPin}
                className="flex items-center gap-0.5 p-1.5 text-gray-300 hover:text-white transition-colors"
                title={t('nav.closeMenu')}
              >
                <ChevronLeft className="w-4 h-4" />
                <Menu className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={toggleSidebarPin}
                className="flex items-center gap-0.5 p-1.5 text-gray-300 hover:text-white transition-colors group"
                title={t('nav.openMenu')}
              >
                <ChevronRight className="w-4 h-4" />
                <Menu className="w-5 h-5" />
                <ChevronRight className="w-3 h-3 text-gray-500 group-hover:text-gray-300 animate-[nudge-right_1.5s_ease-in-out_infinite]" />
              </button>
            )}
          </div>

          <nav className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
            {/* Main nav items (exclude Dashboard & Projects - they're in header) */}
            {mainNavItems.filter(item => item.to !== '/' && item.to !== '/projects').map((item) => {
              const isActive = item.to === '/'
                ? location.pathname === '/'
                : location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  data-onboarding={onboardingKeys[item.to]}
                  title={!sidebarExpanded ? item.label : undefined}
                  className={cn(
                    'flex items-center gap-3 text-sm font-medium transition-colors relative whitespace-nowrap border-b border-[#666]',
                    sidebarExpanded ? 'px-4 py-3' : 'px-0 py-3 justify-center',
                    isActive
                      ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {sidebarExpanded && (
                    <span className="flex-1 truncate">{item.label}</span>
                  )}
                  {'badge' in item && item.badge && (
                    <span className={cn(
                      'flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full',
                      sidebarExpanded ? 'w-5 h-5' : 'absolute -top-0.5 -right-0.5 w-4 h-4 text-[10px]'
                    )}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {/* Settings */}
            {bottomNavItems.map((item) => {
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  title={!sidebarExpanded ? item.label : undefined}
                  className={cn(
                    'flex items-center gap-3 text-sm font-medium transition-colors relative whitespace-nowrap border-b border-[#666]',
                    sidebarExpanded ? 'px-4 py-3' : 'px-0 py-3 justify-center',
                    isActive
                      ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {sidebarExpanded && (
                    <span className="flex-1 truncate">{item.label}</span>
                  )}
                </NavLink>
              );
            })}

            {/* Admin section accordion */}
            {hasAdminSection && (
              <>
                <button
                  onClick={toggleAdminOpen}
                  title={!sidebarExpanded ? t('nav.admin') : undefined}
                  className={cn(
                    'flex items-center gap-3 text-sm font-medium transition-colors border-b border-[#666] whitespace-nowrap',
                    sidebarExpanded ? 'px-4 py-3' : 'px-0 py-3 justify-center',
                    isOnAdminPage
                      ? 'bg-[#666] text-white'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <ShieldCheck className="w-5 h-5 shrink-0" />
                  {sidebarExpanded && (
                    <>
                      <span className="flex-1 truncate text-left">{t('nav.admin')}</span>
                      <ChevronDown className={cn('w-4 h-4 transition-transform', adminOpen ? '' : '-rotate-90')} />
                    </>
                  )}
                </button>
                {adminOpen && sidebarExpanded && (
                  <div className="flex flex-col bg-[#4a4a4a]">
                    {adminNavItems.map((item) => {
                      const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={cn(
                            'flex items-center gap-3 pl-9 pr-4 py-2.5 text-sm font-medium transition-colors border-b border-[#555] whitespace-nowrap',
                            isActive
                              ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                              : 'text-gray-300 hover:bg-[#5a5a5a] hover:text-white'
                          )}
                        >
                          <item.icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1 truncate text-[13px]">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                {!sidebarExpanded && adminOpen && adminNavItems.length > 0 && (
                  <div className="flex flex-col bg-[#4a4a4a]">
                    {adminNavItems.map((item) => {
                      const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          title={item.label}
                          className={cn(
                            'flex items-center justify-center transition-colors px-0 py-2 border-b border-[#555]',
                            isActive
                              ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                              : 'text-gray-400 hover:bg-[#5a5a5a] hover:text-white'
                          )}
                        >
                          <item.icon className="w-4 h-4" />
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </nav>
        </aside>

        {/* Mobile sidebar - full overlay (same dark style) */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 bg-[#555] transform transition-transform lg:hidden',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <div className="flex items-center justify-between h-12 px-4 border-b border-[#666]">
            <h1 className="text-lg font-bold text-white">ProcBase</h1>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)} aria-label={t('nav.closeMenu')}>
              <X className="w-5 h-5 text-gray-300" />
            </button>
          </div>
          <nav className="flex flex-col overflow-y-auto h-[calc(100vh-3rem)]">
            {/* Main nav */}
            {mainNavItems.map((item) => {
              const isActive = item.to === '/'
                ? location.pathname === '/'
                : location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  end={item.to === '/'}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-[#666]',
                    isActive
                      ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  {'badge' in item && item.badge && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
              );
            })}

            {/* Settings */}
            {bottomNavItems.map((item) => {
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors border-b border-[#666]',
                    isActive
                      ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              );
            })}

            {/* Admin section */}
            {hasAdminSection && (
              <>
                <button
                  onClick={toggleAdminOpen}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors w-full border-b border-[#666]',
                    isOnAdminPage
                      ? 'bg-[#666] text-white'
                      : 'text-gray-200 hover:bg-[#666] hover:text-white'
                  )}
                >
                  <ShieldCheck className="w-5 h-5 shrink-0" />
                  <span className="flex-1 text-left">{t('nav.admin')}</span>
                  <ChevronDown className={cn('w-4 h-4 transition-transform', adminOpen ? '' : '-rotate-90')} />
                </button>
                {adminOpen && (
                  <div className="flex flex-col bg-[#4a4a4a]">
                    {adminNavItems.map((item) => {
                      const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setSidebarOpen(false)}
                          className={cn(
                            'flex items-center gap-3 pl-9 pr-4 py-2.5 text-sm font-medium transition-colors border-b border-[#555]',
                            isActive
                              ? 'bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100'
                              : 'text-gray-300 hover:bg-[#5a5a5a] hover:text-white'
                          )}
                        >
                          <item.icon className="w-4 h-4 shrink-0" />
                          <span className="flex-1 text-[13px]">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </nav>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Project Header Bar — hidden on Dashboard & Projects pages */}
          {!hideSidebar && (
            <div className="h-10 flex items-center px-4 lg:px-6 bg-gray-800 dark:bg-gray-900 border-b border-gray-700 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setProjectPopupOpen(true)}
                className="flex items-center gap-2.5 text-gray-200 hover:text-white transition-colors min-w-0 group cursor-pointer rounded-lg px-2 py-1 -ml-2 hover:bg-gray-700/60"
              >
                {selectedProject ? (
                  <>
                    <div className="w-6 h-6 rounded-full bg-gray-600 group-hover:bg-gray-500 flex items-center justify-center text-white font-bold text-xs shrink-0 transition-colors">
                      {(selectedProject.client_name || selectedProject.title).charAt(0)}
                    </div>
                    <span className="text-sm font-medium truncate">
                      {selectedProject.client_name
                        ? `${selectedProject.client_name}\u21D4${selectedProject.title}`
                        : selectedProject.title}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-200 shrink-0 transition-colors" />
                  </>
                ) : (
                  <>
                    <div className="w-6 h-6 rounded-full bg-gray-600 group-hover:bg-gray-500 flex items-center justify-center text-gray-400 group-hover:text-gray-200 font-bold text-xs shrink-0 transition-colors">
                      ?
                    </div>
                    <span className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors">プロジェクトを選択</span>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 group-hover:text-gray-300 shrink-0 transition-colors" />
                  </>
                )}
              </button>
              <div className="flex-1" />
              <button
                onClick={handleProjectSearch}
                className="p-1.5 text-gray-400 hover:text-gray-200 transition-colors"
                title="検索"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          )}

          <main className="flex-1 overflow-auto p-4 lg:p-6 bg-gray-50 dark:bg-gray-950 flex flex-col scrollbar-thin">
            <div className="flex-1">
              <Outlet />
            </div>
            <footer className="mt-4 pb-2 text-center text-xs text-gray-400 dark:text-gray-600">
              <p>© {new Date().getFullYear()} エー・アール・システム株式会社</p>
              <p className="mt-0.5">
                ProcBase v1.0.0
                <span className="mx-1">·</span>
                Build: {(() => {
                  try {
                    return new Date(__BUILD_TIME__).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
                  } catch {
                    return __BUILD_TIME__;
                  }
                })()}
              </p>
            </footer>
          </main>
        </div>
      </div>

      {/* Project Selector Popup */}
      {projectPopupOpen && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={() => setProjectPopupOpen(false)} />

          {/* Popup content - top-aligned, full width */}
          <div className="relative w-full max-w-4xl mx-auto mt-12 bg-gray-700 dark:bg-gray-800 rounded-xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-5 py-3 text-white font-bold text-sm bg-gray-600 dark:bg-gray-700">
              プロジェクト
            </div>

            {/* Search bar + sort */}
            <div className="px-4 py-2 border-b border-gray-600 dark:border-gray-700 flex items-center gap-2">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                value={projectSearchInput}
                onChange={e => setProjectSearchInput(e.target.value)}
                placeholder="プロジェクトを検索"
                className="flex-1 bg-transparent text-gray-200 text-sm outline-none placeholder-gray-400"
                autoFocus
              />
              {projectSearchInput && (
                <button onClick={() => setProjectSearchInput('')} className="text-gray-400 hover:text-gray-200">
                  <X className="w-4 h-4" />
                </button>
              )}
              <select
                value={projectSort}
                onChange={e => setProjectSort(e.target.value as 'default' | 'priority' | 'due_date')}
                className="text-xs bg-gray-600 dark:bg-gray-700 text-gray-200 border border-gray-500 dark:border-gray-600 rounded px-1.5 py-1 ml-1"
              >
                <option value="default">更新順</option>
                <option value="priority">優先度順</option>
                <option value="due_date">期限順</option>
              </select>
            </div>

            {/* Project list */}
            <div className="overflow-y-auto flex-1">
              {activeProjects.map(p => renderProjectRow(p, false))}
              {doneProjects.length > 0 && (
                <>
                  <div className="px-5 py-2 bg-gray-600/30 dark:bg-gray-700/30 border-b border-gray-600/50">
                    <span className="text-xs text-gray-400 font-medium">完了・キャンセル ({doneProjects.length})</span>
                  </div>
                  {doneProjects.map(p => renderProjectRow(p, true))}
                </>
              )}
              {filteredPopupProjects.length === 0 && (
                <div className="px-5 py-8 text-center text-gray-400 text-sm">
                  該当するプロジェクトがありません
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <CommandPalette />
      <KeyboardShortcutsHelp />
      <QuickActionFAB />
      <AiChatWidget />
      <OnboardingOverlay />
    </div>
  );
}
